import { useMemo, useRef, useState } from "react";
import {
  CheckCircle2, CreditCard, Download, FileText, Image as ImageIcon,
  Loader2, Pencil, UploadCloud, Users,
} from "lucide-react";
import { ErrorState } from "#/components/common/ErrorState";
import { PageLoader } from "#/components/common/PageLoader";
import { useEmployeeOptions } from "#/features/employees/hooks";
import { businessCardApi, downloadBlob } from "../api";
import { useActivateCardTemplate, useCardTemplates, useUploadCardTemplate } from "../hooks";
import { TemplateFieldEditor } from "../components/TemplateFieldEditor";
import type { CardFieldPage, CardTemplate } from "../types";

export default function BusinessCardPage() {
  const { data: templates, isLoading, isError, error } = useCardTemplates();
  const [editingId, setEditingId] = useState<number | null>(null);

  if (isLoading && !templates) return <PageLoader label="Loading business card templates…" />;
  if (isError) return <ErrorState title="Failed to load templates" message={error?.message} />;

  const activeTemplate = templates?.find((t) => t.active) ?? null;
  const editingTemplate = templates?.find((t) => t.id === editingId) ?? null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-black text-slate-900">Business Card</h1>
      </div>

      <TemplateSection
        templates={templates ?? []}
        editingTemplate={editingTemplate}
        onEdit={(id) => setEditingId((cur) => (cur === id ? null : id))}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <SingleEmployeeSection activeTemplate={activeTemplate} />
        <AllEmployeesSection activeTemplate={activeTemplate} />
      </div>
    </div>
  );
}

// ─── Templates ───────────────────────────────────────────────────────────────

function TemplateSection({
  templates,
  editingTemplate,
  onEdit,
}: {
  templates: CardTemplate[];
  editingTemplate: CardTemplate | null;
  onEdit: (id: number) => void;
}) {
  const upload = useUploadCardTemplate();
  const activate = useActivateCardTemplate();
  const [name, setName] = useState("");
  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  async function handleUpload() {
    setFormError(null);
    if (!name.trim()) return setFormError("Give the template a name.");
    if (!front) return setFormError("Choose the front-page PDF.");
    try {
      await upload.mutateAsync({ name: name.trim(), front, back: back ?? undefined });
      setName("");
      setFront(null);
      setBack(null);
      if (frontRef.current) frontRef.current.value = "";
      if (backRef.current) backRef.current.value = "";
    } catch (err: any) {
      setFormError(err?.response?.data?.message ?? "Upload failed.");
    }
  }

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-slate-800">Card template</h2>
      </div>

      {/* Upload form */}
      <div className="mb-4 grid gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name"
          className="rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-emerald-500"
        />
        <label className="flex items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 text-xs text-slate-600">
          Front PDF
          <input
            ref={frontRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFront(e.target.files?.[0] ?? null)}
            className="w-full text-[11px]"
          />
        </label>
        <label className="flex items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 text-xs text-slate-600">
          Back PDF (optional)
          <input
            ref={backRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setBack(e.target.files?.[0] ?? null)}
            className="w-full text-[11px]"
          />
        </label>
        <button
          type="button"
          onClick={handleUpload}
          disabled={upload.isPending}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
          Upload
        </button>
        {formError && <p className="text-xs font-medium text-rose-600 sm:col-span-4">{formError}</p>}
      </div>

      {/* Template list */}
      {templates.length === 0 ? (
        <p className="text-xs text-slate-400">No templates uploaded yet.</p>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 p-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {t.active ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
                      Inactive
                    </span>
                  )}
                  <span className="text-sm font-semibold text-slate-800">{t.name}</span>
                  <span className="text-[11px] text-slate-400">
                    {t.fields.length} field{t.fields.length === 1 ? "" : "s"} · {t.hasBackPage ? "front + back" : "front only"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {!t.active && (
                    <button
                      type="button"
                      onClick={() => activate.mutate(t.id)}
                      disabled={activate.isPending}
                      className="rounded-lg border border-emerald-200 px-2.5 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    >
                      Activate
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onEdit(t.id)}
                    className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="h-3 w-3" /> {editingTemplate?.id === t.id ? "Close" : "Edit fields"}
                  </button>
                </div>
              </div>

              {editingTemplate?.id === t.id && (
                <div className="mt-3 border-t pt-3">
                  <TemplateFieldEditor template={editingTemplate} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Single employee ─────────────────────────────────────────────────────────

function SingleEmployeeSection({ activeTemplate }: { activeTemplate: CardTemplate | null }) {
  const { data: employeeOptions } = useEmployeeOptions();
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState<number | "">("");
  const [previewPage, setPreviewPage] = useState<CardFieldPage>("front");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState<"pdf" | "png" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employeeOptions ?? [];
    return (employeeOptions ?? []).filter(
      (e) => e.fullName.toLowerCase().includes(q) || e.employeeNumber.toLowerCase().includes(q),
    );
  }, [employeeOptions, search]);

  async function loadPreview(id: number, page: CardFieldPage) {
    setError(null);
    setLoadingPreview(true);
    try {
      const blob = await businessCardApi.employeeCardPng(id, page);
      setPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return window.URL.createObjectURL(blob);
      });
    } catch {
      setError("Could not render a preview for this employee.");
      setPreviewUrl(null);
    } finally {
      setLoadingPreview(false);
    }
  }

  function handleSelect(id: number) {
    setEmployeeId(id);
    setPreviewPage("front");
    loadPreview(id, "front");
  }

  function handlePageToggle(page: CardFieldPage) {
    setPreviewPage(page);
    if (employeeId) loadPreview(employeeId, page);
  }

  async function handleDownload(kind: "pdf" | "png") {
    if (!employeeId) return;
    setDownloading(kind);
    setError(null);
    try {
      const blob = kind === "pdf"
        ? await businessCardApi.employeeCardPdf(employeeId)
        : await businessCardApi.employeeCardPng(employeeId, previewPage);
      downloadBlob(blob, `business-card-${employeeId}.${kind}`);
    } catch {
      setError(`Could not download the ${kind.toUpperCase()}.`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <FileText className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-slate-800">Single employee</h2>
      </div>

      {!activeTemplate && (
        <p className="mb-3 text-xs font-medium text-amber-600">
          No active template
        </p>
      )}

      <div className="space-y-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or employee number…"
          className="w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none focus:border-emerald-500"
        />
        <select
          value={employeeId}
          onChange={(e) => handleSelect(Number(e.target.value))}
          disabled={!activeTemplate}
          className="w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none focus:border-emerald-500 disabled:opacity-50"
        >
          <option value="">— Select an employee —</option>
          {filtered.map((e) => (
            <option key={e.id} value={e.id}>{e.employeeNumber} — {e.fullName}</option>
          ))}
        </select>
      </div>

      {employeeId !== "" && (
        <div className="mt-3 space-y-2">
          {activeTemplate?.hasBackPage && (
            <div className="flex gap-1">
              {(["front", "back"] as CardFieldPage[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => handlePageToggle(p)}
                  className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold capitalize ${
                    previewPage === p ? "border-emerald-500 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-2">
            {loadingPreview ? (
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            ) : previewUrl ? (
              <img src={previewUrl} alt="Card preview" className="max-h-[220px] rounded shadow" />
            ) : (
              <span className="text-xs text-slate-400">No preview yet.</span>
            )}
          </div>

          {error && <p className="text-xs font-medium text-rose-600">{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleDownload("pdf")}
              disabled={downloading !== null}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {downloading === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Download PDF
            </button>
            <button
              type="button"
              onClick={() => handleDownload("png")}
              disabled={downloading !== null}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {downloading === "png" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
              Download image
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── All employees ───────────────────────────────────────────────────────────

function AllEmployeesSection({ activeTemplate }: { activeTemplate: CardTemplate | null }) {
  const { data: employeeOptions } = useEmployeeOptions();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardEnabledCount = (employeeOptions ?? []).filter((e) => e.businessCardEnabled).length;
  const pageCount = Math.max(1, Math.ceil(cardEnabledCount / 5));

  async function handleDownload() {
    setDownloading(true);
    setError(null);
    try {
      const blob = await businessCardApi.sheetPdf();
      downloadBlob(blob, "business-cards-sheet.pdf");
    } catch {
      setError("Could not generate the A4 sheet.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-slate-800">All employees</h2>
      </div>

      {!activeTemplate && (
        <p className="mb-3 text-xs font-medium text-amber-600">
          No active template
        </p>
      )}

      <p className="text-sm text-slate-600">
        <span className="font-bold text-slate-900">{cardEnabledCount}</span> employee
        {cardEnabledCount === 1 ? "" : "s"} have business cards enabled — 5 per A4 page, so this
        generates <span className="font-bold text-slate-900">{pageCount}</span> page{pageCount === 1 ? "" : "s"}
        {activeTemplate?.hasBackPage ? " per side (front and back)" : ""}.
      </p>

      {error && <p className="mt-2 text-xs font-medium text-rose-600">{error}</p>}

      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading || !activeTemplate || cardEnabledCount === 0}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        Download A4 sheet (PDF)
      </button>
    </div>
  );
}
